import React, { useState, useEffect } from 'react';
import { apiGet } from '../lib/api';
import { UserTracking, ActivityLog } from '../types';
import { UserAvatar } from './UserAvatar';
import { useLanguage } from '../lib/i18n';
import { formatGregorianDateTime } from '../lib/dateUtils';
import { 
  Users, Search, Calendar, Clock, ChevronLeft, RefreshCw, Eye, 
  MapPin, AlertCircle, Sparkles, LayoutGrid, ListFilter, UserCheck
} from 'lucide-react';

const getTabLabel = (tab: string, lang: string): string => {
  if (lang === 'sv') {
    switch (tab) {
      case 'dashboard': return 'Hälsoinstrumentpanel 🏠';
      case 'rehab': return 'Ryggrehabilitering 🩺';
      case 'belly-fat': return 'Magfett Kondition 🔥';
      case 'nutrition': return 'Näring & Måltider 🍎';
      case 'weight-tracker': return 'Vikt & BMI-spårare ⚖️';
      case 'swedish': return 'Svenska Studier 🇸🇪';
      case 'schedules': return 'Scheman (Arbete & SFI) 📅';
      case 'activities': return 'Aktivitetslogg 📝';
      case 'notes': return 'Anteckningar & Humör ✍️';
      case 'reports': return 'Rapporter & AI-Export 📊';
      case 'branding': return 'Apptema & Färger 🎨';
      case 'settings': return 'Inställningar & Påminnelser ⚙️';
      case 'users-admin': return 'Användarhantering 🛡️';
      default: return tab;
    }
  }
  if (lang === 'en') {
    switch (tab) {
      case 'dashboard': return 'Health Dashboard 🏠';
      case 'rehab': return 'Spine Rehab 🩺';
      case 'belly-fat': return 'Belly Fat Cardio 🔥';
      case 'nutrition': return 'Nutrition & Meals 🍎';
      case 'weight-tracker': return 'Weight & BMI Tracker ⚖️';
      case 'swedish': return 'Swedish Learning 🇸🇪';
      case 'schedules': return 'Schedules (Work & SFI) 📅';
      case 'activities': return 'Activity Logs 📝';
      case 'notes': return 'Notes & Mood ✍️';
      case 'reports': return 'Reports & AI Export 📊';
      case 'branding': return 'Theme & Colors 🎨';
      case 'settings': return 'Settings & Reminders ⚙️';
      case 'users-admin': return 'User Admin 🛡️';
      default: return tab;
    }
  }
  switch (tab) {
    case 'dashboard': return 'داشبورد سلامت 🏠';
    case 'rehab': return 'توانبخشی ستون فقرات 🩺';
    case 'belly-fat': return 'کاردیو چربی شکم 🔥';
    case 'nutrition': return 'ثبت و پیگیری تغذیه 🍎';
    case 'weight-tracker': return 'پایش وزن و شاخص BMI ⚖️';
    case 'swedish': return 'یادگیری زبان سوئدی 🇸🇪';
    case 'schedules': return 'برنامه‌ها (کار و SFI) 📅';
    case 'activities': return 'ثبت فعالیت‌های متفرقه 📝';
    case 'notes': return 'یادداشت‌ها و خلق‌و‌خو ✍️';
    case 'reports': return 'گزارش‌گیری و خروجی هوش مصنوعی 📊';
    case 'branding': return 'پوسته و رنگ‌های برنامه 🎨';
    case 'settings': return 'تنظیمات و یادآورها ⚙️';
    case 'users-admin': return 'مدیریت و پایش کاربران 🛡️';
    default: return tab;
  }
};

const getTabColor = (tab: string) => {
  switch (tab) {
    case 'dashboard': return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400';
    case 'rehab': return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400';
    case 'belly-fat': return 'bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400';
    case 'nutrition': return 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300';
    case 'weight-tracker': return 'bg-indigo-50 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300';
    case 'swedish': return 'bg-yellow-50 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-400';
    case 'schedules': return 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-400';
    case 'activities': return 'bg-pink-50 text-pink-700 dark:bg-pink-950/40 dark:text-pink-400';
    case 'notes': return 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400';
    case 'reports': return 'bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400';
    case 'branding': return 'bg-fuchsia-50 text-fuchsia-700 dark:bg-fuchsia-950/40 dark:text-fuchsia-400';
    default: return 'bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-400';
  }
};

const formatUserDisplayName = (name?: string, email?: string, lang: string = 'fa'): string => {
  if (
    name && 
    name.trim() !== '' && 
    name !== 'کاربر' && 
    name !== 'کاربر پیشرفت' && 
    name !== 'پیشرفت' && 
    name !== 'کاربر بدون نام' && 
    name !== 'کاربر مهمان'
  ) {
    return name;
  }
  if (email && email.includes('@')) {
    const username = email.split('@')[0];
    return username
      .split(/[._-]/)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }
  return lang === 'sv' ? 'Systemanvändare' : lang === 'en' ? 'System User' : 'کاربر سیستم';
};

export default function UsersAdminModule() {
  const { lang, t } = useLanguage();
  const [users, setUsers] = useState<UserTracking[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<UserTracking | null>(null);
  const [userLogs, setUserLogs] = useState<ActivityLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState<boolean>(false);
  const [pageFilter, setPageFilter] = useState<string>('all');

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const usersRes = await apiGet<any[]>('/api/system/users');
      const logsRes = await apiGet<any[]>('/api/system/all-activity-logs');

      const allUsersRes = usersRes.success && Array.isArray(usersRes.data) ? usersRes.data : [];
      const logsResult = logsRes.success && Array.isArray(logsRes.data) ? logsRes.data : [];

      const userMap = new Map<string, UserTracking>();

      // 1. Add all registered users from local REST API users
      (allUsersRes as any[]).forEach((u: any) => {
        if (!u.id) return;
        const email = u.email || '';
        const photoURL = u.photoURL || u.photo_url || '';
        const displayName = formatUserDisplayName(u.displayName || u.display_name, email);
        userMap.set(u.id, {
          id: u.id,
          uid: u.id,
          email,
          displayName,
          photoURL,
          lastActive: u.lastActive || u.last_login_at || u.joinedAt || '',
          lastPage: u.lastPage || '',
          joinedAt: u.joinedAt || u.created_at || '',
        });
      });

      // 2. Add or enrich from activity logs
      (logsResult as any[]).forEach((log: any) => {
        const uId = log.userId;
        if (!uId) return;

        const details = log.details || {};
        const logEmail = details.email || '';
        const logPhoto = details.photoURL || details.photo_url || '';
        const logName = details.displayName || details.display_name || '';
        const logPage = log.action?.replace('visit:', '') || details.page || '';
        const logTime = log.timestamp || log.createdAt || '';

        const existing = userMap.get(uId);
        if (existing) {
          if (!existing.email && logEmail) existing.email = logEmail;
          if (!existing.photoURL && logPhoto) existing.photoURL = logPhoto;

          const currentFormatted = formatUserDisplayName(existing.displayName, existing.email);
          const logFormatted = formatUserDisplayName(logName, logEmail);
          if (logFormatted !== 'کاربر سیستم' && (currentFormatted === 'کاربر سیستم' || logName)) {
            existing.displayName = logFormatted;
          } else {
            existing.displayName = currentFormatted;
          }

          if (logPage) existing.lastPage = logPage;
          if (logTime && (!existing.lastActive || new Date(logTime) > new Date(existing.lastActive))) {
            existing.lastActive = logTime;
          }
        } else {
          userMap.set(uId, {
            id: uId,
            uid: uId,
            email: logEmail || '',
            displayName: formatUserDisplayName(logName, logEmail),
            photoURL: logPhoto || '',
            lastActive: logTime || '',
            lastPage: logPage,
            joinedAt: logTime || '',
          });
        }
      });

      const fetchedUsers = Array.from(userMap.values());
      fetchedUsers.sort((a, b) => new Date(b.lastActive || b.joinedAt || 0).getTime() - new Date(a.lastActive || a.joinedAt || 0).getTime());
      setUsers(fetchedUsers);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(t('adminFetchUsersError', 'خطا در دریافت لیست کاربران.'));
    } finally {
      setLoading(false);
    }
  };

  const fetchUserLogs = async (user: UserTracking) => {
    setLoadingLogs(true);
    setUserLogs([]);
    try {
      const res = await apiGet<any[]>('/api/system/all-activity-logs?userId=' + (user.id || user.uid));
      if (!res.success || !Array.isArray(res.data)) {
        throw new Error(res.error || 'Failed to fetch activity logs');
      }
      const logs = res.data;
      const fetchedLogs: ActivityLog[] = (logs as any[]).map((log: any) => ({
        id: log.id,
        page: log.action?.replace('visit:', '') || '',
        timestamp: log.timestamp || log.created_at || '',
        email: log.details?.email || '',
        displayName: log.details?.displayName || log.details?.display_name || '',
      }));
      fetchedLogs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setUserLogs(fetchedLogs.slice(0, 100));
    } catch (err) {
      console.error('Error fetching user activity logs from REST API:', err);
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSelectUser = (user: UserTracking) => {
    setSelectedUser(user);
    fetchUserLogs(user);
  };

  // Format date helper
  const formatDate = (isoStr: string) => {
    if (!isoStr) return lang === 'sv' ? 'Okänd' : lang === 'en' ? 'Unknown' : 'نامشخص';
    return formatGregorianDateTime(isoStr, lang);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.displayName.toLowerCase().includes(searchQuery.toLowerCase()) || 
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPage = pageFilter === 'all' || user.lastPage === pageFilter;
    return matchesSearch && matchesPage;
  });

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-1">
      
      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-gray-900 p-6 rounded-3xl border border-gray-100 dark:border-gray-800/80 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-500">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900 dark:text-white font-display">{t('adminModuleTitle', 'مدیریت و پایش کاربران سیستم')}</h1>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {t('adminModuleDesc', 'مشاهده لیست کاربران فعال، آخرین زمان ورود، صفحه‌ای که در آن حضور دارند و تاریخچه فعالیت‌ها')}
            </p>
          </div>
        </div>
        
        <button 
          onClick={fetchUsers}
          disabled={loading}
          className="self-start md:self-auto flex items-center gap-2 px-4 py-2 bg-slate-50 hover:bg-slate-100 dark:bg-gray-800 dark:hover:bg-gray-700/80 text-gray-700 dark:text-gray-200 rounded-xl text-xs font-semibold transition-all shadow-2xs cursor-pointer"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          {t('adminRefreshBtn', 'به‌روزرسانی لیست')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Users List Area */}
        <div className="lg:col-span-2 space-y-4">
          
          {/* Filters Bar */}
          <div className="bg-white dark:bg-gray-900 p-4 rounded-3xl border border-gray-100 dark:border-gray-800/80 shadow-2xs flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('adminSearchPlaceholder', 'جستجوی کاربر بر اساس نام یا ایمیل...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-9 pl-3 py-2 text-xs bg-slate-50 dark:bg-gray-950 rounded-xl border-none focus:ring-2 focus:ring-indigo-500 dark:text-white"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <ListFilter className="w-4 h-4 text-gray-400 shrink-0" />
              <select
                value={pageFilter}
                onChange={(e) => setPageFilter(e.target.value)}
                className="text-xs bg-slate-50 dark:bg-gray-950 rounded-xl border-none py-2 pr-8 pl-3 focus:ring-2 focus:ring-indigo-500 dark:text-white"
              >
                <option value="all">{t('adminFilterAllPages', 'همه صفحات')}</option>
                {['dashboard', 'rehab', 'belly-fat', 'nutrition', 'weight-tracker', 'swedish', 'schedules', 'activities', 'notes', 'reports', 'branding', 'settings', 'users-admin'].map((id) => (
                  <option key={id} value={id}>{getTabLabel(id, lang)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Loading or Error states */}
          {loading ? (
            <div className="bg-white dark:bg-gray-900 p-12 rounded-3xl border border-gray-100 dark:border-gray-800/80 shadow-2xs flex flex-col items-center justify-center gap-3">
              <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" />
              <span className="text-xs text-gray-400 font-medium">{t('adminLoadingUsers', 'در حال بارگذاری لیست کاربران فعال...')}</span>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-3xl flex items-start gap-3 text-red-600 dark:text-red-400">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold">{t('adminConnErrorTitle', 'بروز خطا در برقراری ارتباط')}</p>
                <p className="text-[11px] mt-1 leading-relaxed">{error}</p>
              </div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="bg-white dark:bg-gray-900 p-12 rounded-3xl border border-gray-100 dark:border-gray-800/80 shadow-2xs text-center space-y-2">
              <Users className="w-8 h-8 text-gray-300 mx-auto" />
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">{t('adminNoUsersFound', 'کاربری یافت نشد')}</p>
              <p className="text-[10px] text-gray-400">{t('adminNoUsersDesc', 'هیچ کاربری با شرایط جستجوی شما مطابقت ندارد.')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredUsers.map((u) => {
                const isSelected = selectedUser?.uid === u.uid;
                return (
                  <div 
                    key={u.uid}
                    onClick={() => handleSelectUser(u)}
                    className={`p-4 rounded-2xl border transition-all cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                      isSelected 
                        ? 'bg-indigo-50/40 border-indigo-200 dark:bg-indigo-950/10 dark:border-indigo-800' 
                        : 'bg-white border-gray-100/70 hover:border-indigo-100 hover:bg-slate-50/40 dark:bg-gray-900 dark:border-gray-800/80 dark:hover:bg-gray-800/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <UserAvatar 
                        photoURL={u.photoURL} 
                        displayName={u.displayName} 
                        email={u.email} 
                        size="md" 
                      />
                      <div className="space-y-0.5 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-bold text-gray-900 dark:text-white">{u.displayName}</span>
                          {u.email.toLowerCase() === 'salim.zarepour@gmail.com' && (
                            <span className="text-[9px] bg-indigo-500/10 text-indigo-500 font-extrabold px-1.5 py-0.5 rounded-md">{t('adminSysAdminBadge', 'مدیر سیستم 🛡️')}</span>
                          )}
                        </div>
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 block truncate font-mono">{u.email}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2.5 sm:text-left">
                      {u.lastPage && (
                        <span className={`text-[10px] font-semibold px-2 py-1 rounded-lg ${getTabColor(u.lastPage)}`}>
                          {t('adminPageLabel', 'صفحه:')} {getTabLabel(u.lastPage, lang)}
                        </span>
                      )}
                      
                      <div className="text-[10px] text-gray-400 flex items-center gap-1 font-mono">
                        <Clock className="w-3 h-3 text-gray-300" />
                        <span>{formatDate(u.lastActive)}</span>
                      </div>
                      
                      <ChevronLeft className={`w-4 h-4 text-gray-400 shrink-0 hidden sm:block transition-transform ${isSelected ? 'rotate-90 text-indigo-500' : ''}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* User Details & History Area */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-900 p-5 rounded-3xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-5 sticky top-24">
            {selectedUser ? (
              <>
                {/* Selected User Header Card */}
                <div className="text-center pb-4 border-b border-gray-100 dark:border-gray-800 space-y-3">
                  <UserAvatar 
                    photoURL={selectedUser.photoURL} 
                    displayName={selectedUser.displayName} 
                    email={selectedUser.email} 
                    size="xl" 
                    className="mx-auto" 
                  />
                  <div>
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white">{selectedUser.displayName}</h2>
                    <span className="text-[10px] text-gray-400 font-mono block mt-0.5">{selectedUser.email}</span>
                  </div>
                  
                  <div className="flex flex-col gap-1 bg-slate-50 dark:bg-gray-950 p-3 rounded-2xl text-[10px] text-gray-500 space-y-1">
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-400">{t('adminJoinDate', 'تاریخ عضویت:')}</span>
                      <span className="text-gray-700 dark:text-gray-300 font-medium font-mono">{formatDate(selectedUser.joinedAt || '')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-400">{t('adminLastActive', 'آخرین فعالیت:')}</span>
                      <span className="text-gray-700 dark:text-gray-300 font-medium font-mono">{formatDate(selectedUser.lastActive)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-400">{t('adminLastPage', 'آخرین صفحه:')}</span>
                      <span className="text-gray-700 dark:text-gray-300 font-bold">{getTabLabel(selectedUser.lastPage, lang) || t('adminUnknown', 'نامشخص')}</span>
                    </div>
                  </div>
                </div>

                {/* Logs List Timeline */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-extrabold text-gray-900 dark:text-white flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-indigo-500" />
                      {t('adminRecentViews', 'تاریخچه بازدید صفحات اخیر')}
                    </span>
                    <span className="text-[9px] bg-indigo-50 dark:bg-indigo-950/40 text-indigo-500 dark:text-indigo-400 px-1.5 py-0.5 rounded font-extrabold font-mono">
                      {userLogs.length} {t('adminLoggedUnit', 'ثبت شده')}
                    </span>
                  </div>

                  {loadingLogs ? (
                    <div className="py-12 flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="w-5 h-5 text-indigo-500 animate-spin" />
                      <span className="text-[10px] text-gray-400">{t('adminLoadingLogs', 'در حال بارگذاری لاگ فعالیت‌ها...')}</span>
                    </div>
                  ) : userLogs.length === 0 ? (
                    <div className="py-10 text-center text-gray-400 text-[10px] bg-slate-50 dark:bg-gray-950/50 rounded-2xl">
                      {t('adminNoLogs', 'تاریخچه فعالیتی برای این کاربر ثبت نشده است.')}
                    </div>
                  ) : (
                    <div className="max-h-72 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                      {userLogs.map((log) => (
                        <div 
                          key={log.id} 
                          className="flex items-start gap-2.5 p-2 bg-slate-50/50 dark:bg-gray-950/30 rounded-xl border border-gray-100/50 dark:border-gray-800/40 text-[10px]"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0 mt-1.5"></div>
                          <div className="flex-1 space-y-0.5">
                            <div className="flex justify-between items-center">
                              <span className="font-bold text-gray-800 dark:text-gray-200">
                                {getTabLabel(log.page, lang) || t('adminPageView', 'مشاهده صفحه')}
                              </span>
                            </div>
                            <span className="text-[9px] text-gray-400 block font-mono">{formatDate(log.timestamp)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="py-20 text-center space-y-3">
                <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-950/50 rounded-2xl flex items-center justify-center text-indigo-400 mx-auto animate-bounce">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-800 dark:text-gray-300">{t('adminSelectUserPromptTitle', 'انتخاب کاربر جهت پایش ردیابی')}</p>
                  <p className="text-[10px] text-gray-400 max-w-[200px] mx-auto mt-1 leading-relaxed">
                    {t('adminSelectUserPromptDesc', 'روی یکی از کاربران سمت راست کلیک کنید تا جزئیات ورود و صفحات بازدید شده توسط ایشان نمایش داده شود.')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

    </div>
  );
}
